import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Download, Shield, Wallet, Activity, TrendingUp, LineChart as LineIcon, Receipt, User as UserIcon, ScrollText, Cpu } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge, StatCard, PnL, money, fmt } from "../../components/dashboard/Shared";

const CHART_COLORS = ["#00F0FF", "#7000FF", "#00E676", "#FFD600", "#FF3B30", "#FF7A00", "#00A876", "#A855F7", "#EC4899", "#F97316"];
const tooltipStyle = { background: "#121212", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12, color: "#F3F4F6" };
const axis = { stroke: "rgba(255,255,255,0.2)", tick: { fill: "#6B7280", fontSize: 10, fontFamily: "JetBrains Mono" }, tickLine: false, axisLine: false };

const KV = ({ label, value, testId }) => (
  <div className="flex justify-between items-center py-2 border-b border-white/5" data-testid={testId}>
    <span className="label-xs">{label}</span>
    <span className="tabular text-sm text-white text-right max-w-[60%] break-all">{value ?? <span className="text-gray-600">—</span>}</span>
  </div>
);

const SectionTitle = ({ icon: Icon, title, sub, action }) => (
  <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30 flex items-center justify-center"><Icon size={16} className="text-[#00F0FF]" /></div>
      <div>
        <h2 className="font-display text-lg text-white">{title}</h2>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
    {action}
  </div>
);

export default function ClientDetail() {
  const { userId } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);

  const load = useCallback(async (withLive = false) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/client/${userId}`, { params: { live: withLive } });
      setD(data);
      if (data.trading?.live_error) toast.warning(`Live sync warning: ${data.trading.live_error.slice(0, 100)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load client");
    } finally { setBusy(false); }
  }, [userId]);

  useEffect(() => { load(false); }, [load]);

  const refreshLive = async () => { setLive(true); await load(true); setLive(false); };

  if (!d) return <div className="space-y-4"><div className="skeleton h-14 w-64" /><div className="grid grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-24" />)}</div><div className="skeleton h-72" /></div>;

  const t = d.trading;
  const p = d.personal;
  const nav = [
    ["overview", "Overview", UserIcon],
    ["kyc", "KYC", Shield],
    ["mt5", "MT5", Cpu],
    ["trading", "Live Trading", Wallet],
    ["positions", "Positions", Activity],
    ["charts", "Charts", LineIcon],
    ["activity", "Activity Logs", ScrollText],
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Link to="/admin/users" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2" data-testid="client-back-button"><ArrowLeft size={13} /> Back to users</Link>
        <button onClick={refreshLive} disabled={live || busy} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50" data-testid="client-refresh-live-button">
          <RefreshCw size={13} className={live ? "animate-spin" : ""} /> {live ? "Fetching live MetaApi…" : "Refresh Live Data"}
        </button>
      </div>

      {/* Header card */}
      <div className="card-dark p-6 mb-6" data-testid="client-header-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center text-white font-display text-xl shrink-0 overflow-hidden">
            {p.picture ? <img src={p.picture} alt={p.name} className="w-full h-full object-cover" /> : (p.name || p.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-[240px]">
            <h1 className="font-display text-2xl text-white" data-testid="client-name">{p.name || "—"}</h1>
            <p className="text-sm text-gray-400" data-testid="client-email">{p.email}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusBadge status={p.status} /><StatusBadge status={p.role} />
              {p.kyc_status && <span className="text-[10px] px-2 py-0.5 rounded bg-[#00E676]/10 text-[#00E676] uppercase tracking-wider">KYC: {p.kyc_status}</span>}
              {p.email_verified && <span className="text-[10px] px-2 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF] uppercase tracking-wider">Verified</span>}
              <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-gray-400 uppercase tracking-wider">{p.auth_provider}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 min-w-[280px]">
            <StatCard testId="client-header-balance" label="Balance" value={money(t.balance)} />
            <StatCard testId="client-header-equity" label="Equity" value={money(t.equity)} accent="text-[#00F0FF]" />
            <StatCard testId="client-header-floating" label="Floating P&L" value={<PnL value={t.floating_pnl || 0} />} />
            <StatCard testId="client-header-total" label="Total P&L" value={<PnL value={t.total_pnl || 0} />} />
          </div>
        </div>
      </div>

      {/* Sticky sub-nav */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 bg-[#050505]/95 backdrop-blur py-3 border-y border-white/10 mb-6">
        <nav className="flex gap-1 overflow-x-auto text-xs">
          {nav.map(([id, label, Icon]) => (
            <a key={id} href={`#${id}`} data-testid={`sub-nav-${id}`} className="px-3 py-1.5 rounded flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/5 whitespace-nowrap">
              <Icon size={12} /> {label}
            </a>
          ))}
        </nav>
      </div>

      {/* A. Overview */}
      <section id="overview" className="mb-10">
        <SectionTitle icon={UserIcon} title="Personal Information" sub="Contact, identity and access details" />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-dark p-5">
            <KV label="User ID" value={<span className="text-xs">{p.user_id}</span>} testId="kv-user-id" />
            <KV label="Full Name" value={p.name} testId="kv-name" />
            <KV label="Email" value={p.email} testId="kv-email" />
            <KV label="Phone" value={p.phone} testId="kv-phone" />
            <KV label="Country" value={p.country} testId="kv-country" />
            <KV label="Role" value={p.role} testId="kv-role" />
            <KV label="Status" value={p.status} testId="kv-status" />
          </div>
          <div className="card-dark p-5">
            <KV label="Registered On" value={p.registered_at ? String(p.registered_at).slice(0, 16).replace("T", " ") : null} testId="kv-registered" />
            <KV label="Last Login" value={p.last_login_at ? String(p.last_login_at).slice(0, 16).replace("T", " ") : null} testId="kv-last-login" />
            <KV label="Last IP" value={<span className="text-xs">{p.last_login_ip}</span>} testId="kv-last-ip" />
            <KV label="Device / User-Agent" value={<span className="text-xs">{p.last_login_ua ? p.last_login_ua.slice(0, 80) + (p.last_login_ua.length > 80 ? "…" : "") : null}</span>} testId="kv-last-ua" />
            <KV label="Auth Provider" value={p.auth_provider} testId="kv-auth-provider" />
            <KV label="Email Verified" value={p.email_verified ? "Yes" : "No"} testId="kv-email-verified" />
          </div>
        </div>
      </section>

      {/* A2. Onboarding — data submitted after first login */}
      {d.onboarding && (
        <section id="onboarding" className="mb-10" data-testid="onboarding-section">
          <SectionTitle icon={UserIcon} title="Onboarding Details" sub={d.onboarding.completed ? `Completed on ${(d.onboarding.completed_at || "").slice(0,10)}` : "Not completed yet"} />
          {!d.onboarding.completed ? (
            <div className="card-dark p-5 text-sm text-gray-500">User has not completed onboarding yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card-dark p-5">
                <KV label="First Name" value={d.onboarding.first_name} testId="ob-first-name" />
                <KV label="Last Name" value={d.onboarding.last_name} testId="ob-last-name" />
                <KV label="Phone" value={d.onboarding.phone} testId="ob-phone" />
                <KV label="Country" value={d.onboarding.country} testId="ob-country" />
                <KV label="Profession" value={d.onboarding.profession} testId="ob-profession" />
                <KV label="Referred By" value={d.onboarding.referred_by} testId="ob-referred" />
              </div>
              <div className="card-dark p-5">
                <KV label="Trading Experience" value={d.onboarding.trading_experience} testId="ob-experience" />
                <KV label="Current Equity" value={d.onboarding.current_equity !== null && d.onboarding.current_equity !== undefined ? `$${Number(d.onboarding.current_equity).toLocaleString()}` : null} testId="ob-equity" />
                <KV label="Past Trading Losses" value={d.onboarding.total_trading_losses !== null && d.onboarding.total_trading_losses !== undefined ? `$${Number(d.onboarding.total_trading_losses).toLocaleString()}` : null} testId="ob-losses" />
                <KV label="Risk Appetite" value={d.onboarding.risk_appetite} testId="ob-risk" />
                <KV label="Trading Goals" value={<span className="text-xs !whitespace-normal">{d.onboarding.trading_goals}</span>} testId="ob-goals" />
              </div>
            </div>
          )}
        </section>
      )}

      {/* B. KYC */}
      <section id="kyc" className="mb-10">
        <SectionTitle icon={Shield} title="KYC Information" sub="Powered by Didit v3" action={d.kyc.download_url ? <a href={d.kyc.download_url} target="_blank" rel="noreferrer" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2" data-testid="kyc-download-button"><Download size={12} /> Download document</a> : null} />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-dark p-5">
            <KV label="Status" value={<StatusBadge status={d.kyc.status} />} testId="kyc-status" />
            <KV label="Verified At" value={d.kyc.verified_at ? String(d.kyc.verified_at).slice(0, 16).replace("T", " ") : null} testId="kyc-verified-at" />
            <KV label="Provider" value={d.kyc.provider} testId="kyc-provider" />
            <KV label="Document Type" value={d.kyc.document_type} testId="kyc-doc-type" />
            <KV label="Document # (masked)" value={<span className="text-xs">{d.kyc.document_number_masked}</span>} testId="kyc-doc-number" />
          </div>
          <div className="card-dark p-5">
            <KV label="First Name" value={d.kyc.first_name} testId="kyc-first-name" />
            <KV label="Last Name" value={d.kyc.last_name} testId="kyc-last-name" />
            <KV label="Date of Birth" value={d.kyc.date_of_birth} testId="kyc-dob" />
            <KV label="Country" value={d.kyc.country} testId="kyc-country" />
            <KV label="Selfie Verified" value={d.kyc.selfie_verified === true ? "Yes" : d.kyc.selfie_verified === false ? "No" : null} testId="kyc-selfie" />
            <KV label="Address Verified" value={d.kyc.address_verified === true ? "Yes" : d.kyc.address_verified === false ? "No" : null} testId="kyc-address" />
          </div>
        </div>
        {d.kyc.audit_logs && d.kyc.audit_logs.length > 0 && (
          <div className="mt-5">
            <p className="label-xs mb-3">KYC Audit Trail</p>
            <Table headers={["Action", "Detail", "IP", "At"]} testId="kyc-audit-table">
              {d.kyc.audit_logs.map((a, i) => (
                <tr key={i} className="border-b border-white/5"><Td className="text-xs text-white">{a.action}</Td><Td className="text-xs text-gray-500">{a.detail}</Td><Td className="text-xs tabular">{a.ip || "—"}</Td><Td className="text-xs tabular text-gray-500">{String(a.created_at).slice(0, 16).replace("T", " ")}</Td></tr>
              ))}
            </Table>
          </div>
        )}
      </section>

      {/* C. MT5 */}
      <section id="mt5" className="mb-10">
        <SectionTitle icon={Cpu} title="MT5 Account Information" sub="Broker credentials and MetaApi connectivity" />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-dark p-5">
            <KV label="Broker" value={d.mt5.broker} testId="mt5-broker" />
            <KV label="Login" value={<span className="tabular">{d.mt5.login}</span>} testId="mt5-login" />
            <KV label="Server" value={d.mt5.server} testId="mt5-server" />
            <KV label="Account Currency" value={d.mt5.currency} testId="mt5-currency" />
            <KV label="Leverage" value={d.mt5.leverage ? `1:${d.mt5.leverage}` : null} testId="mt5-leverage" />
            <KV label="Account Type" value={d.mt5.account_type ? String(d.mt5.account_type).toUpperCase() : null} testId="mt5-account-type" />
            <KV label="Declared Equity (form)" value={d.mt5.declared_equity != null ? `$${Number(d.mt5.declared_equity).toLocaleString()}` : null} testId="mt5-declared-equity" />
            <KV label="Broker-Verified Equity" value={d.mt5.connected && d.mt5.verified_equity != null ? `$${Number(d.mt5.verified_equity).toLocaleString()}` : "Not verified"} testId="mt5-verified-equity" />
          </div>
          <div className="card-dark p-5">
            <KV label="Connection Status" value={<StatusBadge status={d.mt5.connection_status} />} testId="mt5-connection-status" />
            <KV label="Deployment State" value={d.mt5.deployment_state} testId="mt5-deployment-state" />
            <KV label="Sync State" value={d.mt5.sync_state} testId="mt5-sync-state" />
            <KV label="MetaApi Account" value={<span className="text-xs">{d.mt5.metaapi_account_id}</span>} testId="mt5-metaapi-id" />
            <KV label="Connected At" value={d.mt5.connected_at ? String(d.mt5.connected_at).slice(0, 16).replace("T", " ") : null} testId="mt5-connected-at" />
            <KV label="Last Sync" value={d.mt5.last_synced_at ? String(d.mt5.last_synced_at).slice(0, 16).replace("T", " ") : null} testId="mt5-last-sync" />
            <KV label="Trading Status" value={d.mt5.trading_status} testId="mt5-trading-status" />
            {d.mt5.last_error && <KV label="Last Error" value={<span className="text-xs text-[#FF3B30]">{d.mt5.last_error.slice(0, 200)}</span>} />}
          </div>
        </div>
      </section>

      {/* D. Live trading */}
      <section id="trading" className="mb-10">
        <SectionTitle icon={Wallet} title="Live Trading Information" sub="Real balances and performance metrics" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
          <StatCard testId="trading-balance" label="Balance" value={money(t.balance)} />
          <StatCard testId="trading-equity" label="Equity" value={money(t.equity)} accent="text-[#00F0FF]" />
          <StatCard testId="trading-margin" label="Margin" value={money(t.margin)} />
          <StatCard testId="trading-free-margin" label="Free Margin" value={money(t.free_margin)} />
          <StatCard testId="trading-margin-level" label="Margin Level %" value={t.margin_level != null ? `${fmt(t.margin_level)}%` : "—"} />
          <StatCard testId="trading-floating" label="Floating P&L" value={<PnL value={t.floating_pnl || 0} />} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard testId="trading-today" label="Today's P&L" value={<PnL value={t.today_pnl || 0} />} />
          <StatCard testId="trading-week" label="Weekly P&L" value={<PnL value={t.week_pnl || 0} />} />
          <StatCard testId="trading-month" label="Monthly P&L" value={<PnL value={t.month_pnl || 0} />} />
          <StatCard testId="trading-total" label="Total P&L" value={<PnL value={t.total_pnl || 0} />} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard testId="trading-max-dd" label="Max Drawdown" value={`${fmt(t.max_drawdown_pct)}%`} accent="text-[#FFD600]" />
          <StatCard testId="trading-current-dd" label="Current Drawdown" value={`${fmt(t.current_drawdown_pct)}%`} accent="text-[#FFD600]" />
          <StatCard testId="trading-win-rate" label="Win Rate" value={`${fmt(t.win_rate_pct)}%`} accent="text-[#00E676]" />
          <StatCard testId="trading-profit-factor" label="Profit Factor" value={fmt(t.profit_factor)} accent="text-[#00F0FF]" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testId="trading-avg-win" label="Average Win" value={money(t.avg_win)} accent="text-[#00E676]" />
          <StatCard testId="trading-avg-loss" label="Average Loss" value={money(t.avg_loss)} accent="text-[#FF3B30]" />
          <StatCard testId="trading-largest-win" label="Largest Win" value={money(t.largest_win)} accent="text-[#00E676]" />
          <StatCard testId="trading-largest-loss" label="Largest Loss" value={money(t.largest_loss)} accent="text-[#FF3B30]" />
        </div>
      </section>

      {/* E. Positions */}
      <section id="positions" className="mb-10">
        <SectionTitle icon={Activity} title="Position Information" sub={`${d.positions.open_count} open · ${d.positions.closed_count} closed`} />
        <div className="grid lg:grid-cols-2 gap-6 mb-5">
          <div>
            <p className="label-xs mb-3">Open Positions</p>
            {d.positions.open_positions_live.length === 0 ? (
              <div className="card-dark p-6 text-center text-sm text-gray-500">No open positions.</div>
            ) : (
              <Table headers={["Symbol", "Type", "Lots", "Open", "SL", "TP", "P&L"]} testId="positions-open-table">
                {d.positions.open_positions_live.map((p) => (
                  <tr key={p.id} className="border-b border-white/5">
                    <Td className="tabular text-white">{p.symbol}</Td>
                    <Td><span className={p.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{(p.type || "").toUpperCase()}</span></Td>
                    <Td className="tabular">{fmt(p.volume)}</Td>
                    <Td className="tabular">{p.open_price}</Td>
                    <Td className="tabular text-xs text-[#FF3B30]">{p.sl || "—"}</Td>
                    <Td className="tabular text-xs text-[#00E676]">{p.tp || "—"}</Td>
                    <Td><PnL value={p.profit || 0} /></Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
          <div>
            <p className="label-xs mb-3">Symbol-wise Exposure (open)</p>
            {d.positions.symbol_exposure.length === 0 ? (
              <div className="card-dark p-6 text-center text-sm text-gray-500">No exposure.</div>
            ) : (
              <Table headers={["Symbol", "Total Lots"]} testId="positions-exposure-table">
                {d.positions.symbol_exposure.map((e) => (
                  <tr key={e.symbol} className="border-b border-white/5"><Td className="tabular text-white">{e.symbol}</Td><Td className="tabular">{fmt(e.lots)}</Td></tr>
                ))}
              </Table>
            )}
          </div>
        </div>
        {d.positions.trade_history.length > 0 && (
          <>
            <p className="label-xs mb-3">Recent Trade History (last 50 of {d.positions.closed_count})</p>
            <Table headers={["Symbol", "Type", "Lots", "Open", "Close", "Close Time", "P&L"]} testId="positions-history-table">
              {d.positions.trade_history.slice(0, 50).map((t) => (
                <tr key={t.trade_id} className="border-b border-white/5">
                  <Td className="tabular text-white">{t.symbol}</Td>
                  <Td><span className={t.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{(t.type || "").toUpperCase()}</span></Td>
                  <Td className="tabular">{fmt(t.volume)}</Td>
                  <Td className="tabular">{t.open_price}</Td>
                  <Td className="tabular">{t.close_price}</Td>
                  <Td className="tabular text-xs text-gray-500">{String(t.close_time || "").slice(0, 16).replace("T", " ")}</Td>
                  <Td><PnL value={t.profit || 0} /></Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </section>

      {/* F. Charts */}
      <section id="charts" className="mb-10">
        <SectionTitle icon={LineIcon} title="Performance Charts" sub="Equity, drawdown, distribution and returns" />
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card-dark p-5" data-testid="chart-equity-curve">
            <p className="label-xs mb-3">Equity Curve</p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={d.charts.equity_curve}>
                <defs><linearGradient id="cd-eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity={0.3} /><stop offset="100%" stopColor="#00F0FF" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={40} />
                <YAxis {...axis} width={70} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="equity" stroke="#00F0FF" strokeWidth={1.8} fill="url(#cd-eq)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-balance-curve">
            <p className="label-xs mb-3">Balance Curve</p>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.charts.equity_curve}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={40} />
                <YAxis {...axis} width={70} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="balance" stroke="#7000FF" strokeWidth={1.8} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-daily-pnl">
            <p className="label-xs mb-3">Daily P&L</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.charts.daily_pnl.slice(-60)}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={40} />
                <YAxis {...axis} width={60} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="profit" radius={[3, 3, 0, 0]}>{d.charts.daily_pnl.slice(-60).map((r, i) => <Cell key={i} fill={r.profit >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-monthly-pnl">
            <p className="label-xs mb-3">Monthly P&L</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.charts.monthly_pnl}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" {...axis} />
                <YAxis {...axis} width={60} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="profit" radius={[4, 4, 0, 0]}>{d.charts.monthly_pnl.map((r, i) => <Cell key={i} fill={r.profit >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-drawdown">
            <p className="label-xs mb-3">Drawdown</p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={d.charts.drawdown}>
                <defs><linearGradient id="cd-dd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFD600" stopOpacity={0.4} /><stop offset="100%" stopColor="#FFD600" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={40} />
                <YAxis {...axis} width={60} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="drawdown" stroke="#FFD600" strokeWidth={1.6} fill="url(#cd-dd)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-cumulative">
            <p className="label-xs mb-3">Cumulative Return</p>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.charts.cumulative_return}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={40} />
                <YAxis {...axis} width={70} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="cumulative" stroke="#00E676" strokeWidth={1.8} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-distribution">
            <p className="label-xs mb-3">Trade Distribution (by P&L bucket)</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.charts.trade_distribution}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="bucket" {...axis} />
                <YAxis {...axis} width={40} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#00F0FF" fillOpacity={0.7} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-dark p-5" data-testid="chart-symbol-profit">
            <p className="label-xs mb-3">Symbol-wise Profit</p>
            {d.charts.symbol_wise_profit.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">No closed trades yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={d.charts.symbol_wise_profit.slice(0, 10)} layout="vertical">
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" {...axis} tickFormatter={(v) => `$${v}`} />
                  <YAxis type="category" dataKey="symbol" {...axis} width={80} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="pnl">{d.charts.symbol_wise_profit.slice(0, 10).map((r, i) => <Cell key={i} fill={r.pnl >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      {/* G. Profit Sharing */}
      <section id="profit" className="mb-10">
        <SectionTitle icon={Receipt} title="Profit Sharing" sub="Fees, payments and dues" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <StatCard testId="profit-generated" label="Total Fees Generated" value={money(d.profit_sharing.total_fees_generated)} accent="text-[#00F0FF]" />
          <StatCard testId="profit-paid" label="Total Fees Paid" value={money(d.profit_sharing.total_fees_paid)} accent="text-[#00E676]" />
          <StatCard testId="profit-pending" label="Pending Fees" value={money(d.profit_sharing.pending_fees)} accent="text-[#FFD600]" />
          <StatCard testId="profit-method" label="Last Payment Method" value={d.profit_sharing.preferred_payment_method || "—"} />
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card-dark p-4"><p className="label-xs">Last Payment Period</p><p className="tabular text-white mt-2">{d.profit_sharing.last_payment_at || "—"}</p></div>
          <div className="card-dark p-4"><p className="label-xs">Next Due Period</p><p className="tabular text-[#FFD600] mt-2">{d.profit_sharing.next_due_period || "—"}</p></div>
        </div>
        <p className="label-xs mb-3">Invoice History</p>
        {d.profit_sharing.invoice_history.length === 0 ? (
          <div className="card-dark p-6 text-center text-sm text-gray-500">No invoices generated yet.</div>
        ) : (
          <Table headers={["Invoice", "Period", "Fee", "Status", "Method", "Amount Due", "Submitted"]} testId="profit-invoices-table">
            {d.profit_sharing.invoice_history.map((i) => (
              <tr key={i.invoice_id} className="border-b border-white/5">
                <Td className="tabular text-xs">{i.invoice_id}</Td>
                <Td className="tabular">{i.period}</Td>
                <Td className="tabular">{money(i.fee_amount)}</Td>
                <Td><StatusBadge status={i.status} /></Td>
                <Td className="text-xs">{i.payment_method || "—"}</Td>
                <Td className="tabular">{i.amount_due ? money(i.amount_due) : "—"}</Td>
                <Td className="text-xs tabular text-gray-500">{i.payment_submitted_at ? String(i.payment_submitted_at).slice(0, 16).replace("T", " ") : "—"}</Td>
              </tr>
            ))}
          </Table>
        )}
      </section>

      {/* Subscription */}
      <section id="subscription" className="mb-10">
        <SectionTitle icon={TrendingUp} title="Subscription Information" sub="Plan and account linkage" />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-dark p-5">
            <KV label="Current Plan" value={d.subscription.current_plan} testId="sub-plan" />
            <KV label="Connected Accounts" value={d.subscription.connected_accounts} testId="sub-accounts" />
          </div>
          <div className="card-dark p-5">
            <KV label="Start Date" value={d.subscription.start_date ? String(d.subscription.start_date).slice(0, 10) : null} testId="sub-start" />
            <KV label="End Date" value={d.subscription.end_date ? String(d.subscription.end_date).slice(0, 10) : null} testId="sub-end" />
            <KV label="Auto Renewal" value={d.subscription.auto_renewal ? "Yes" : "No"} testId="sub-auto-renewal" />
          </div>
        </div>
      </section>

      {/* I. Activity Logs */}
      <section id="activity" className="mb-10">
        <SectionTitle icon={ScrollText} title="Activity Logs" sub="Login, MT5, KYC, executions, payments, admin actions" />
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <p className="label-xs mb-3">Login History ({d.activity.login_history.length})</p>
            <Table headers={["When", "Detail", "IP"]} testId="activity-login-table">
              {d.activity.login_history.length === 0 ? <tr><td colSpan="3" className="text-center text-xs text-gray-500 py-4">No login history</td></tr> :
                d.activity.login_history.slice(0, 20).map((a, i) => (
                  <tr key={i} className="border-b border-white/5"><Td className="text-xs tabular text-gray-500">{String(a.created_at).slice(0, 16).replace("T", " ")}</Td><Td className="text-xs">{a.detail}</Td><Td className="text-xs tabular">{a.ip || "—"}</Td></tr>
                ))}
            </Table>
          </div>
          <div>
            <p className="label-xs mb-3">MT5 Connection History ({d.activity.mt5_history.length})</p>
            <Table headers={["When", "Action", "Detail"]} testId="activity-mt5-table">
              {d.activity.mt5_history.length === 0 ? <tr><td colSpan="3" className="text-center text-xs text-gray-500 py-4">No MT5 activity</td></tr> :
                d.activity.mt5_history.slice(0, 20).map((a, i) => (
                  <tr key={i} className="border-b border-white/5"><Td className="text-xs tabular text-gray-500">{String(a.created_at).slice(0, 16).replace("T", " ")}</Td><Td className="text-xs text-white">{a.action}</Td><Td className="text-xs">{a.detail}</Td></tr>
                ))}
            </Table>
          </div>
          <div>
            <p className="label-xs mb-3">Trade Executions ({d.activity.trade_executions.length})</p>
            <Table headers={["When", "Symbol", "Lots", "Status"]} testId="activity-executions-table">
              {d.activity.trade_executions.length === 0 ? <tr><td colSpan="4" className="text-center text-xs text-gray-500 py-4">No executions</td></tr> :
                d.activity.trade_executions.slice(0, 20).map((e) => (
                  <tr key={e.exec_id} className="border-b border-white/5"><Td className="text-xs tabular text-gray-500">{String(e.created_at).slice(0, 16).replace("T", " ")}</Td><Td className="tabular text-xs text-white">{e.symbol || "ALL"}</Td><Td className="tabular text-xs">{e.lots ?? "—"}</Td><Td><StatusBadge status={e.status === "success" ? "paid" : e.status === "failed" ? "overdue" : "pending"} /></Td></tr>
                ))}
            </Table>
          </div>
          <div>
            <p className="label-xs mb-3">Payment Logs ({d.activity.payment_logs.length})</p>
            <Table headers={["When", "Action", "Detail"]} testId="activity-payments-table">
              {d.activity.payment_logs.length === 0 ? <tr><td colSpan="3" className="text-center text-xs text-gray-500 py-4">No payment activity</td></tr> :
                d.activity.payment_logs.slice(0, 20).map((a, i) => (
                  <tr key={i} className="border-b border-white/5"><Td className="text-xs tabular text-gray-500">{String(a.created_at).slice(0, 16).replace("T", " ")}</Td><Td className="text-xs text-white">{a.action}</Td><Td className="text-xs">{a.detail}</Td></tr>
                ))}
            </Table>
          </div>
        </div>
      </section>
    </div>
  );
}
